import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { combineLatest, Observable, of, startWith } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import {
  AddProjectRequest,
  FinanceService,
  ProjectAutocompleteSuggestions,
  ProjectDetailsRow,
  ReferenceEntry,
  UpdateProjectRequest,
} from '../../../finance.service';
import { ToasterService } from '@app/shared/services/toaster.service';

export interface AddProjectsModalData {
  /** Pre-loaded reference data so the modal opens without an additional fetch. */
  businessSegmentOptions: ReferenceEntry[];
  supportingLobOptions: ReferenceEntry[];
  /** Distinct existing values for the free-text autocomplete fields
   *  (Product Suite, Agile Team, SVP, VP, Functional Manager, Invoice Approver). */
  autocompleteSuggestions: ProjectAutocompleteSuggestions;
  /** 'add' shows the form for creation; 'edit' pre-populates from
   *  `existing` and exposes the Status field. */
  mode: 'add' | 'edit';
  /** Pre-filled row when mode === 'edit'. Ignored in 'add' mode. */
  existing?: ProjectDetailsRow;
}

/**
 * Project create / edit modal. The Figma is the same 2-column form for
 * both flows; Edit just adds a Status field (Active / Inactive). One
 * component serves both modes so the layout stays in lock-step.
 */
@Component({
  selector: 'app-add-projects-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
  ],
  templateUrl: './add-projects-modal.component.html',
  styleUrl: './add-projects-modal.component.scss',
})
export class AddProjectsModalComponent implements OnInit {
  form!: FormGroup;
  saving = false;
  errorMessage: string | null = null;

  filteredBusinessSegments$!: Observable<ReferenceEntry[]>;
  filteredSupportingLobs$!: Observable<ReferenceEntry[]>;
  filteredProductSuites$!: Observable<string[]>;
  filteredAgileTeams$!: Observable<string[]>;
  filteredSvps$!: Observable<string[]>;
  filteredVps$!: Observable<string[]>;
  filteredFunctionalManagers$!: Observable<string[]>;
  filteredInvoiceApprovers$!: Observable<string[]>;

  get isEdit(): boolean {
    return this.data?.mode === 'edit';
  }

  get title(): string {
    return this.isEdit ? 'Edit Project' : 'Add Projects';
  }

  get saveLabel(): string {
    if (this.saving) return 'Saving...';
    return this.isEdit ? 'Update' : 'Save';
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AddProjectsModalData,
    private readonly dialogRef: MatDialogRef<AddProjectsModalComponent, ProjectDetailsRow | null>,
    private readonly fb: FormBuilder,
    private readonly financeService: FinanceService,
    private readonly toaster: ToasterService,
  ) {}

  /**
   * Suggestions stream shared across all 6 free-text autocomplete inputs.
   * Initialised in ngOnInit because field initialisers can't reference
   * injected services. shareReplay(1) caches the fetched value so each
   * filter stream gets it (and any future subscribers do too).
   * catchError + of(…) keeps the modal usable if the call fails —
   * autocomplete falls back to empty lists; free typing still works.
   */
  private suggestions$!: Observable<ProjectAutocompleteSuggestions>;

  ngOnInit(): void {
    // Seed the stream with whatever the parent pre-loaded so the
    // autocomplete dropdowns have data the instant the modal opens.
    // The modal then re-fetches fresh data; any new values land
    // transparently via shareReplay. If parent hadn't loaded yet (cold
    // open within ms of page load), the seed is an empty-arrays object
    // and the user sees suggestions populate once the fetch resolves.
    const EMPTY_SUGGESTIONS: ProjectAutocompleteSuggestions = {
      productSuites: [],
      agileTeams: [],
      svps: [],
      vps: [],
      functionalManagers: [],
      invoiceApprovers: [],
    };
    const seed = this.data?.autocompleteSuggestions ?? EMPTY_SUGGESTIONS;
    this.suggestions$ = this.financeService.getProjectAutocompleteSuggestions().pipe(
      catchError(() => of(seed)),
      startWith(seed),
      shareReplay(1),
    );

    const existing = this.data?.existing;
    this.form = this.fb.group({
      businessSegment: [existing?.businessSegment ?? ''],
      supportingLob: [existing?.supportingLob ?? ''],
      productSuite: [existing?.productSuite ?? ''],
      agileTeam: [existing?.agileTeam ?? ''],
      projectName: [
        existing?.projectName ?? '',
        [Validators.required, Validators.maxLength(255)],
      ],
      projectIdCode: [
        existing?.projectIdCode ?? '',
        [Validators.required, Validators.maxLength(64)],
      ],
      svp: [existing?.svp ?? ''],
      vp: [existing?.vp ?? ''],
      functionalManager: [existing?.functionalManager ?? ''],
      invoiceApprover: [existing?.invoiceApprover ?? ''],
      glBusinessUnit: [existing?.glBusinessUnit ?? ''],
      billToFu: [existing?.billToFu ?? ''],
      billToMo: [existing?.billToMo ?? ''],
      // Status only relevant in edit mode; defaults to Active for new
      // records server-side. Even in add mode we keep the control in the
      // form so the shape is stable — it just isn't rendered.
      status: [existing?.status ?? 'Active', this.isEdit ? [Validators.required] : []],
    });

    this.filteredBusinessSegments$ = this.makeRefStream(
      'businessSegment',
      this.data?.businessSegmentOptions ?? [],
    );
    this.filteredSupportingLobs$ = this.makeRefStream(
      'supportingLob',
      this.data?.supportingLobOptions ?? [],
    );
    // Free-text autocomplete fields source their options from the BE
    // suggestions stream so they re-filter once the fetch resolves —
    // avoids the snapshot-at-modal-open race condition.
    this.filteredProductSuites$ = this.makeStringStream(
      'productSuite',
      this.suggestions$.pipe(map((s) => s.productSuites)),
    );
    this.filteredAgileTeams$ = this.makeStringStream(
      'agileTeam',
      this.suggestions$.pipe(map((s) => s.agileTeams)),
    );
    this.filteredSvps$ = this.makeStringStream(
      'svp',
      this.suggestions$.pipe(map((s) => s.svps)),
    );
    this.filteredVps$ = this.makeStringStream(
      'vp',
      this.suggestions$.pipe(map((s) => s.vps)),
    );
    this.filteredFunctionalManagers$ = this.makeStringStream(
      'functionalManager',
      this.suggestions$.pipe(map((s) => s.functionalManagers)),
    );
    this.filteredInvoiceApprovers$ = this.makeStringStream(
      'invoiceApprover',
      this.suggestions$.pipe(map((s) => s.invoiceApprovers)),
    );
  }

  private makeRefStream(
    controlName: string,
    options: ReferenceEntry[],
  ): Observable<ReferenceEntry[]> {
    const control = this.form.get(controlName);
    if (!control) {
      throw new Error(`Form control "${controlName}" is missing.`);
    }
    return control.valueChanges.pipe(
      startWith(''),
      map((typed) => (typed ?? '').toString().trim().toLowerCase()),
      map((needle) =>
        needle
          ? options.filter(
              (o) =>
                o.code.toLowerCase().includes(needle) ||
                o.label.toLowerCase().includes(needle),
            )
          : options.slice(),
      ),
    );
  }

  /**
   * combineLatest the form-control value with the BE-sourced options
   * stream so the filtered list re-emits when either side changes:
   * user typing OR suggestions arriving asynchronously.
   */
  private makeStringStream(
    controlName: string,
    options$: Observable<string[]>,
  ): Observable<string[]> {
    const control = this.form.get(controlName);
    if (!control) {
      throw new Error(`Form control "${controlName}" is missing.`);
    }
    const value$ = control.valueChanges.pipe(startWith(control.value ?? ''));
    return combineLatest([value$, options$]).pipe(
      map(([typed, opts]) => {
        const needle = (typed ?? '').toString().trim().toLowerCase();
        return needle ? opts.filter((o) => o.toLowerCase().includes(needle)) : opts.slice();
      }),
    );
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    this.errorMessage = null;
    const formValue = this.form.value;

    if (this.isEdit) {
      const id = this.data?.existing?.projectId;
      if (id == null) {
        this.errorMessage = 'Missing project id — cannot update.';
        this.saving = false;
        return;
      }
      const payload: UpdateProjectRequest = { ...formValue };
      this.financeService.updateProject(id, payload).subscribe({
        next: (updated) => this.handleSuccess(updated, 'updated'),
        error: (err) => this.handleError(err),
      });
    } else {
      // Drop status from the add payload — BE defaults to Active and
      // doesn't expect the field for create.
      const { status: _ignored, ...createPayload } = formValue;
      const payload: AddProjectRequest = createPayload;
      this.financeService.createProject(payload).subscribe({
        next: (created) => this.handleSuccess(created, 'added'),
        error: (err) => this.handleError(err),
      });
    }
  }

  private handleSuccess(row: ProjectDetailsRow, verb: 'added' | 'updated'): void {
    this.saving = false;
    this.toaster.showSuccess(
      `Project "${row.projectName ?? row.projectIdCode}" ${verb}.`,
      'Project Details',
    );
    this.dialogRef.close(row);
  }

  /**
   * Helpers consumed by the template to decide whether to render the
   * "+ Add 'X' as new" option at the bottom of each autocomplete panel.
   * Shown when the user has typed a value that doesn't exactly match any
   * existing suggestion — the typed string is then explicitly addable.
   */
  typedValue(controlName: string): string {
    const v = this.form?.get(controlName)?.value;
    return (v ?? '').toString().trim();
  }

  isNewStringValue(controlName: string, opts: string[] | null): boolean {
    const typed = this.typedValue(controlName);
    if (!typed) return false;
    const lowered = typed.toLowerCase();
    return !(opts ?? []).some((o) => o.toLowerCase() === lowered);
  }

  isNewRefValue(controlName: string, opts: ReferenceEntry[] | null): boolean {
    const typed = this.typedValue(controlName);
    if (!typed) return false;
    const lowered = typed.toLowerCase();
    return !(opts ?? []).some(
      (o) => o.code.toLowerCase() === lowered || o.label.toLowerCase() === lowered,
    );
  }

  private handleError(err: { status?: number; error?: { message?: string; error?: string }; message?: string }): void {
    this.saving = false;
    const beMessage =
      err?.error?.message || err?.error?.error || err?.message || null;
    const action = this.isEdit ? 'update' : 'add';
    const fallback = `Failed to ${action} project. Please try again.`;

    if (err?.status === 400 && beMessage) {
      this.errorMessage = beMessage;
    } else if (err?.status === 404) {
      this.errorMessage = 'Project not found.';
    } else {
      this.errorMessage = fallback;
    }
  }
}
